<template>
  <div class="page-container">
    <Sidebar />
    
    <div class="main-content">
      <div class="category-page">
        <h1 class="page-title">漫画分类</h1>
        
        <!-- 筛选区域 -->
        <div class="filter-section">
          <div class="filter-row">
            <div class="filter-label">作品类型:</div>
            <div class="filter-options">
              <span 
                v-for="category in categories" 
                :key="category.id"
                :class="['filter-option', { active: selectedCategoryId === category.id }]"
                @click="selectCategory(category.id)"
              >
                {{ category.name }}
              </span>
            </div>
          </div>
          
          <div class="filter-row">
            <div class="filter-label">付费模式:</div>
            <div class="filter-options">
              <span 
                v-for="option in paymentOptions" 
                :key="option.value"
                :class="['filter-option', { active: selectedPayment === option.value }]"
                @click="selectPayment(option.value)"
              >
                {{ option.label }}
              </span>
            </div>
          </div>
        </div>
        
        <!-- 加载中状态 -->
        <div v-if="loading" class="loading">
          <div class="spinner"></div>
          <p>加载中...</p>
        </div>
        
        <!-- 错误状态 -->
        <div v-else-if="error" class="error-message">
          <p>{{ error }}</p>
          <button @click="fetchComics" class="retry-btn">重试</button>
        </div>
        
        <!-- 漫画列表 -->
        <div v-else class="comics-container">
          <div v-if="comics.length === 0" class="empty-state">
            <p>暂无符合条件的漫画</p>
          </div>
          
          <div v-else class="comics-grid">
            <div 
              v-for="comic in comics" 
              :key="comic.id" 
              class="comic-card"
              @click="navigateToComic(comic.id)"
            >
              <div class="comic-cover">
                <img 
                  :src="getFullImageUrl(comic.cover)" 
                  :alt="comic.title"
                  @error="handleImageError"
                />
                <span v-if="comic.is_free === 1" class="free-tag">免费</span>
              </div>
              <div class="comic-info">
                <h3 class="comic-title">{{ comic.title }}</h3>
                <p class="comic-author">{{ comic.author }}</p>
                <p class="comic-meta">更新时间：{{ formatTimestamp(comic.updatetime) }}</p>
                <p class="comic-meta">浏览量：{{ comic.views || 0 }}</p>
                <p class="comic-desc">{{ truncateText(comic.description, 50) }}</p>
              </div>
            </div>
          </div>
          
          <!-- 分页 -->
          <div v-if="totalPages > 1" class="pagination">
            <button 
              :disabled="currentPage === 1" 
              @click="changePage(currentPage - 1)"
              class="page-btn"
            >
              上一页
            </button>
            
            <span class="page-info">{{ currentPage }} / {{ totalPages }}</span>
            
            <button 
              :disabled="currentPage === totalPages" 
              @click="changePage(currentPage + 1)"
              class="page-btn"
            >
              下一页
            </button>
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  </div>
</template>

<script>
import { ref, onMounted, watch } from 'vue';
import { useRouter } from 'vue-router';
import { getFilteredComics } from '@/api/comic';
import { getCategoryList } from '@/api/category';
import { getApiUrl } from '@/api/config';
import Sidebar from '@/components/layout/Sidebar.vue';
import Footer from '@/components/layout/Footer.vue';

export default {
  name: 'CategoryPage',
  components: {
    Sidebar,
    Footer
  },
  setup() {
    const router = useRouter();
    const comics = ref([]);
    const categories = ref([]);
    const loading = ref(true);
    const error = ref('');
    const currentPage = ref(1);
    const totalPages = ref(1);
    const limit = 10; // 每页显示数量
    
    // 筛选选项
    const paymentOptions = ref([
      { label: '全部', value: 0 },
      { label: '免费', value: 1 },
      { label: '付费', value: 2 }
    ]);
    
    // 当前选中的筛选条件
    const selectedCategoryId = ref(0); // 0表示全部
    const selectedPayment = ref(0);
    
    // 获取分类列表
    const fetchCategories = async () => {
      try {
        const response = await getCategoryList();
        if (response.code === 1 && response.data && response.data.list) {
          // 添加"全部"选项
          categories.value = [
            { id: 0, name: '全部' },
            ...response.data.list
          ];
        } else {
          console.error('获取分类列表失败:', response.msg);
          categories.value = [{ id: 0, name: '全部' }];
        }
      } catch (err) {
        console.error('获取分类列表出错:', err);
        categories.value = [{ id: 0, name: '全部' }];
      }
    };
    
    // 获取漫画列表
    const fetchComics = async (page = 1) => {
      loading.value = true;
      error.value = '';
      
      try {
        const filters = {
          categoryId: selectedCategoryId.value === 0 ? null : selectedCategoryId.value,
          isFree: selectedPayment.value
        };
        
        const result = await getFilteredComics(filters, page, limit);
        
        comics.value = result.list;
        currentPage.value = result.page;
        totalPages.value = result.pages;
      } catch (err) {
        error.value = '网络错误，请稍后重试';
        console.error('获取漫画列表失败:', err);
      } finally {
        loading.value = false;
      }
    };
    
    // 切换页码
    const changePage = (page) => {
      if (page < 1 || page > totalPages.value) return;
      currentPage.value = page;
      fetchComics(page);
    };
    
    // 选择分类
    const selectCategory = (categoryId) => {
      if (selectedCategoryId.value === categoryId) return;
      selectedCategoryId.value = categoryId;
      currentPage.value = 1;
      fetchComics(1);
    };
    
    // 选择付费模式
    const selectPayment = (value) => {
      if (selectedPayment.value === value) return;
      selectedPayment.value = value;
      currentPage.value = 1;
      fetchComics(1);
    };
    
    // 获取完整的图片URL
    const getFullImageUrl = (path) => {
      if (!path) return '';
      if (path.startsWith('http')) return path;
      return getApiUrl(path);
    };
    
    // 处理图片加载错误
    const handleImageError = (e) => {
      e.target.src = '';
    };
    
    // 截断文本
    const truncateText = (text, length) => {
      if (!text) return '';
      return text.length > length ? text.substring(0, length) + '...' : text;
    };

    const formatTimestamp = (timestamp) => {
      if (!timestamp) return '-';
      const date = new Date(timestamp * 1000);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    // 跳转到漫画详情页
    const navigateToComic = (id) => {
      router.push(`/comic/${id}`);
    };
    
    // 监听筛选条件变化
    watch([selectedCategoryId, selectedPayment], () => {
      currentPage.value = 1;
      fetchComics(1);
    }, { immediate: false });
    
    onMounted(async () => {
      await fetchCategories();
      fetchComics();
    });
    
    return {
      comics,
      categories,
      loading,
      error,
      currentPage,
      totalPages,
      paymentOptions,
      selectedCategoryId,
      selectedPayment,
      fetchComics,
      changePage,
      selectCategory,
      selectPayment,
      getFullImageUrl,
      handleImageError,
      truncateText,
      formatTimestamp,
      navigateToComic
    };
  }
};
</script>

<style scoped>
.page-container {
  display: flex;
  min-height: 100vh;
  background-color: #f5f5f5;
}

.main-content {
  flex: 1;
  margin-left: 200px; /* 侧边栏宽度 */
  padding: 20px;
}

.category-page {
  max-width: 1200px;
  margin: 0 auto;
}

.page-title {
  font-size: 24px;
  font-weight: bold;
  margin-bottom: 20px;
  color: #333;
  text-align: center;
}

/* 筛选区域样式 */
.filter-section {
  background-color: #fff;
  border-radius: 8px;
  padding: 15px 20px;
  margin-bottom: 20px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);
}

.filter-row {
  display: flex;
  align-items: center;
  padding: 10px 0;
  border-bottom: 1px solid #f0f0f0;
}

.filter-row:last-child {
  border-bottom: none;
}

.filter-label {
  width: 80px;
  color: #666;
  font-size: 14px;
}

.filter-options {
  display: flex;
  flex-wrap: wrap;
  flex: 1;
}

.filter-option {
  padding: 4px 12px;
  margin: 0 8px 8px 0;
  border-radius: 16px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s;
  background-color: #f5f5f5;
  color: #666;
}

.filter-option:hover {
  background-color: #f0f0f0;
}

.filter-option.active {
  background-color: #ff6b81;
  color: white;
}

/* 加载状态 */
.loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 0;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid rgba(255, 107, 129, 0.3);
  border-radius: 50%;
  border-top-color: #ff6b81;
  animation: spin 1s linear infinite;
  margin-bottom: 10px;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.error-message {
  text-align: center;
  padding: 30px;
  color: #e74c3c;
}

.retry-btn {
  background-color: #ff6b81;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  margin-top: 10px;
}

/* 漫画列表 */
.comics-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 20px;
}

.comic-card {
  background-color: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s, box-shadow 0.3s;
  cursor: pointer;
}

.comic-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
}

.comic-cover {
  height: 280px;
  overflow: hidden;
  position: relative;
  background-color: #f5f5f5;
}

.comic-cover img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s;
}

.comic-card:hover .comic-cover img {
  transform: scale(1.05);
}

.free-tag {
  position: absolute;
  top: 10px;
  right: 10px;
  background-color: #ff6b81;
  color: white;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 12px;
}

.comic-info {
  padding: 12px;
}

.comic-title {
  font-size: 16px;
  font-weight: bold;
  margin-bottom: 5px;
  color: #333;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.comic-author {
  font-size: 12px;
  color: #666;
  margin-bottom: 5px;
}

.comic-meta {
  font-size: 12px;
  color: #888;
  margin-bottom: 4px;
}

.comic-desc {
  font-size: 12px;
  color: #999;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-height: 1.4;
}

.empty-state {
  text-align: center;
  padding: 40px 0;
  color: #666;
}

.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 30px;
}

.page-btn {
  background-color: #f0f0f0;
  border: none;
  padding: 8px 15px;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.3s;
}

.page-btn:hover:not(:disabled) {
  background-color: #e0e0e0;
}

.page-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.page-info {
  margin: 0 15px;
  color: #666;
}
</style> 